<template>
  <main class="flyer-page print-friendly">
    <div class="flyer-container">
      <h1>Laptop Drop-Off Location</h1>

      <!-- Header: Logo and QR Code -->
      <div class="header-section">
        <div class="logo-section">
          <img src="/assets/img/logo.png" alt="Space Coast STEM Logo" class="logo" />
        </div>
        <div class="qr-section">
          <canvas ref="qrCodeCanvas" class="qr-canvas"></canvas>
        </div>
      </div>

      <!-- Info Section -->
      <div class="info-section">
        <p class="info-text">
          Space Coast STEM refurbishes donated laptops and gives them to <strong>local students in Brevard County who need access to technology at home</strong>.
        </p>

        <div class="accept-section">
          <h2>What We Accept</h2>
          <div class="accept-grid">
            <div class="accept-item featured">
              <div class="featured-content">
                <div class="item-icon">💻</div>
                <div class="featured-text">
                  <strong>Laptops</strong>
                  <ul class="requirements">
                    <li>Working Screen & Battery</li>
                    <li>4GB RAM or More</li>
                    <li>With or Without Hard Drive/SSD</li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="accept-item">
              <div class="item-icon">🔌</div>
              <strong>Power Adapters</strong>
            </div>
            <div class="accept-item">
              <div class="item-icon">🎒</div>
              <strong>Laptop Bags/Backpacks</strong>
            </div>
            <div class="accept-item">
              <div class="item-icon">🖱️</div>
              <strong>Computer Mice</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Contact Box -->
      <div class="contact-box">
        <p>Questions or box full? Email <strong>donations@spacecoaststem.org</strong></p>
      </div>
    </div>
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import QRCode from 'qrcode'

const qrCodeCanvas = ref<HTMLCanvasElement | null>(null)

onMounted(async () => {
  if (qrCodeCanvas.value) {
    try {
      await QRCode.toCanvas(qrCodeCanvas.value, 'https://spacecoaststem.org', {
        width: 280,
        margin: 1,
        color: {
          dark: '#2c4a6b',
          light: '#ffffff'
        }
      })
    } catch (error) {
      console.error('Error generating QR code:', error)
    }
  }
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');

.flyer-page {
  font-family: 'Inter', sans-serif;
  background: white;
  min-height: 100vh;
}

.flyer-container {
  max-width: 8.5in;
  margin: 0 auto;
  padding: 0.5in;
  background: white;
}

/* Title */
h1 {
  font-size: 2.2rem;
  font-weight: 800;
  color: #2c4a6b;
  margin: 0 0 1rem 0;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* Header Section: Logo | QR Code */
.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.2rem;
  gap: 2rem;
}

.logo-section {
  flex: 1;
  display: flex;
  justify-content: center;
}

.logo {
  width: 280px;
  height: auto;
}

.qr-section {
  flex: 1;
  display: flex;
  justify-content: center;
}

.qr-canvas {
  width: 280px;
  height: 280px;
}

/* Info Section */
.info-section {
  margin-bottom: 1.2rem;
}

.info-text {
  font-size: 1.2rem;
  line-height: 1.6;
  color: #333;
  text-align: center;
  margin: 0 0 1.2rem 0;
}

.info-text strong {
  color: #2c4a6b;
}

/* Accept Section */
.accept-section {
  background: linear-gradient(135deg, rgba(44, 74, 107, 0.05) 0%, rgba(59, 163, 209, 0.05) 100%);
  border: 3px solid #3ba3d1;
  border-radius: 12px;
  padding: 1rem;
}

.accept-section h2 {
  font-size: 1.6rem;
  font-weight: 700;
  color: #2c4a6b;
  margin: 0 0 1rem 0;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.accept-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.accept-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: white;
  padding: 1rem;
  border-radius: 10px;
  border: 2px solid #3ba3d1;
  text-align: center;
  min-height: 120px;
}

.accept-item.featured {
  grid-column: 1 / -1;
  border: 3px solid #2c4a6b;
  background: linear-gradient(135deg, rgba(44, 74, 107, 0.03) 0%, rgba(59, 163, 209, 0.03) 100%);
  flex-direction: row;
  justify-content: center;
  padding: 0.8rem 1.5rem;
}

.featured-content {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  justify-content: center;
}

.featured-content .item-icon {
  font-size: 4rem;
  margin: 0;
  flex-shrink: 0;
}

.featured-text {
  text-align: left;
}

.featured-text strong {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 1.3rem;
}

.requirements {
  list-style: none;
  padding: 0;
  margin: 0.3rem 0 0 0;
  text-align: left;
  width: 100%;
}

.requirements li {
  font-size: 0.95rem;
  color: #333;
  margin: 0.3rem 0;
  padding-left: 1.5rem;
  position: relative;
  font-weight: 500;
}

.requirements li::before {
  content: "•";
  position: absolute;
  left: 0.5rem;
  color: #3ba3d1;
  font-weight: bold;
  font-size: 1.2rem;
}

.item-icon {
  font-size: 2.5rem;
  margin-bottom: 0.2rem;
}

.accept-item strong {
  font-size: 1rem;
  color: #2c4a6b;
  line-height: 1.3;
}

.accept-item .sub-text {
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
  font-weight: normal;
}

/* Contact Box */
.contact-box {
  background: #f5f7fa;
  border: 3px solid #2c4a6b;
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
}

.contact-box p {
  font-size: 1.1rem;
  color: #333;
  margin: 0;
  line-height: 1.5;
}

.contact-box strong {
  color: #2c4a6b;
  font-size: 1.2rem;
}

/* Print Styles */
@media print {
  .flyer-page {
    background: white;
  }

  .flyer-container {
    padding: 0.4in;
    max-width: none;
  }

  @page {
    margin: 0.25in;
    size: letter portrait;
  }

  /* Preserve colors and backgrounds */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  /* Prevent page breaks inside elements */
  .header-section,
  .accept-section,
  .accept-item,
  .contact-box,
  .info-section {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Prevent page breaks after headers */
  h1,
  h2 {
    page-break-after: avoid;
    break-after: avoid;
  }

  /* Preserve grid layouts */
  .accept-grid {
    page-break-inside: avoid;
    break-inside: avoid;
  }

  /* Preserve flexbox and grid layouts */
  .header-section {
    display: flex !important;
  }

  .accept-grid {
    display: grid !important;
  }

  /* Ensure QR code and logo display properly */
  .qr-canvas,
  .logo {
    page-break-inside: avoid;
    break-inside: avoid;
  }
}

/* Responsive for screen viewing */
@media (max-width: 768px) {
  .flyer-container {
    padding: 1rem;
  }

  h1 {
    font-size: 1.75rem;
  }

  .header-section {
    flex-direction: column;
  }

  .logo {
    width: 200px;
  }

  .qr-canvas {
    width: 200px;
    height: 200px;
  }

  .info-text {
    font-size: 1.1rem;
  }

  .accept-grid {
    grid-template-columns: 1fr;
  }

  .contact-box p {
    font-size: 1rem;
  }

  .contact-box strong {
    font-size: 1.1rem;
  }
}
</style>
